<template>
  <template v-if="!eventRegistered">
    <h2 class="mb-4 subtitle-2">
      {{ $t.signUp }}
    </h2>
    <base-form class="f-col space-y-4" :form @submit="submit">
      <p class="fw-light">
        {{ replaceAll($t.eventSignUp.title, { title }) }}
      </p>
      <vf-notification
        v-if="notification.type"
        :type="notification.type"
        @close="dismissNotification"
      >
        {{ notification.message }}
      </vf-notification>
      <input-email v-model="email" disabled :required="false" />
      <vf-dropdown
        v-if="eventDates.length >= 2"
        class="relative duration"
        class-button="w-full between rounded-xs h-12"
        class-button-opened="b-b-0 rounded-b-0"
        class-content="w-full f-col gap-4"
        :label="form.attendingDays.length
          ? `${form.attendingDays.length} ${$t.eventSignUp.daysSelected}`
          : `${$t.eventSignUp.attendingDays}`"
        :max-selectable-options="maxSelectableDays"
        :selected-options-count="form.attendingDays.length"
        style-button="background: #fff; border-color: #9a9a9a"
        style-content="border-color: #9a9a9a"
      >
        <div
          v-if="selectionNotification.type"
          ref="selectionNotificationRef"
          aria-live="polite"
          class="mb-4"
          tabindex="-1"
        >
          <vf-notification
            :type="selectionNotification.type"
            @close="dismissSelectionNotification"
          >
            {{ selectionNotification.message }}
          </vf-notification>
        </div>
        <vf-checkbox
          v-for="(day, key) in eventDates"
          :key
          :model-value="form.attendingDays"
          :value="day"
          @update:model-value="updateAttendingDays"
        >
          {{ day }}
        </vf-checkbox>
      </vf-dropdown>
      <vf-form-field
        v-slot="{ attrs }"
        v-if="enablePlusOne"
        name="plusOne"
        :rule="form.plusOne ? [validateName($t.eventSignUp.guestNameValidation)] : []"
      >
        <vf-input v-model="form.plusOne" v-bind="attrs" :disabled="loading" :label="$t.eventSignUp.guestName" />
      </vf-form-field>
      <vf-form-field
        v-slot="{ attrs }"
        name="eventTermsAccepted"
        :rule="validateRequired('', $t.validators.requiredCheckbox)"
      >
        <vf-checkbox v-model="form.eventTermsAccepted" v-bind="attrs" :disabled="loading" required>
          <cms-rich-text v-if="richText" class="m-r-1 i-flex" :content="{ richText }" />
        </vf-checkbox>
      </vf-form-field>
      <vf-button class="w-full" :disabled="submitDisabled" :loading type="submit">
        {{ submitCtaLabel }}
      </vf-button>
    </base-form>
  </template>
  <div v-else>
    <h2 class="mb-4 subtitle-1">
      {{ confirmationTitle }}
    </h2>
    <p class="text-md fw-light">
      {{ confirmationSubtitle }}
    </p>
    <base-dialog-close class="mt-6 w-full" data-test-id="vf-modal-close">
      <vf-button class="w-full">
        {{ $t.close }}
      </vf-button>
    </base-dialog-close>
  </div>
</template>

<script lang="ts" setup>
import type { EventSignUp } from '#types/components/cms/event-signup'
import type { BaseNotification } from '#types/notification'

const { content } = defineProps<{
  content: EventSignUp
}>()

const { $eventRegistrationData } = useApi().consumer
const { validateRequired } = useLocalizedValidators()
const { $t } = useNuxtApp()
const { details } = storeToRefs(useUserStore())
const eventRegistered = ref(false)
const loading = ref(false)

const form = reactive({
  attendingDays: [] as string[],
  eventTermsAccepted: false,
  plusOne: '',
})

const notification = reactive<BaseNotification>({
  type: '',
  message: ''
})

const selectionNotification = reactive<BaseNotification>({
  type: '',
  message: ''
})
const selectionNotificationRef = ref<HTMLElement>()

const {
  confirmationSubtitle,
  confirmationTitle,
  enablePlusOne,
  endDate,
  eventId,
  maxDays,
  richText,
  startDate,
  submitCtaLabel,
  title
} = content

const email = computed(() => details.value?.email)
const maxSelectableDays = computed(() => {
  const parsedMaxDays = Number(maxDays)

  if (!parsedMaxDays || parsedMaxDays < 1)
    return undefined

  return parsedMaxDays
})

const submitDisabled = computed(() =>
  loading.value
  || (!!maxSelectableDays.value && form.attendingDays.length > maxSelectableDays.value)
)

const dismissNotification = () => {
  notification.type = ''
  notification.message = ''
}

const dismissSelectionNotification = () => {
  selectionNotification.type = ''
  selectionNotification.message = ''
}

const eventDates = computed(() => {
  const dates = [] as string[]
  for (let day = new Date(startDate); day <= new Date(endDate); day.setDate(day.getDate() + 1)) {
    dates.push(day.toDateString())
  }
  return dates
})

const showSelectionWarning = async () => {
  if (!maxSelectableDays.value)
    return

  const maxDaysWarning = ($t.eventSignUp as Record<string, string>).maxDaysWarning

  selectionNotification.type = 'info'
  selectionNotification.message = replaceAll(
    maxDaysWarning,
    { count: String(maxSelectableDays.value) }
  )

  await nextTick()
  selectionNotificationRef.value?.scrollIntoView({ block: 'nearest' })
  selectionNotificationRef.value?.focus()
}

const updateAttendingDays = async (value: boolean | string[] | undefined) => {
  if (!isArray(value))
    return

  if (maxSelectableDays.value && value.length > maxSelectableDays.value) {
    await showSelectionWarning()
    return
  }

  dismissSelectionNotification()
  form.attendingDays = value
}

const submit = async () => {
  if (maxSelectableDays.value && form.attendingDays.length > maxSelectableDays.value) {
    await showSelectionWarning()
    return
  }

  try {
    loading.value = true
    const { attendingDays, eventTermsAccepted, plusOne } = form

    await $eventRegistrationData({
      email: details.value?.email || '',
      attendingDays: attendingDays.join(', '),
      eventId,
      eventTermsAccepted,
      firstName: details.value?.firstName || '',
      lastName: details.value?.lastName || '',
      gender: details.value?.gender || '',
      phoneNumber: details.value?.phone || '',
      plusOne,
      registrationTimestamp: new Date().toISOString()
    })
    eventRegistered.value = true
  }
  catch (err) {
    if (isError(err)) {
      notification.type = 'error'
      notification.message = err.message
    }
  }
  finally {
    loading.value = false
  }
}
</script>
